import type { IconProp } from '@atlaskit/button/new';
import VideoClipIcon from '@atlaskit/icon-lab/core/video-clip';
import CommentIcon from '@atlaskit/icon/core/comment';
import LinkIcon from '@atlaskit/icon/core/link';
import PageIcon from '@atlaskit/icon/core/page';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import SearchIcon from '@atlaskit/icon/core/search';
import TaskIcon from '@atlaskit/icon/core/task';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common';

import { type MetricSelectionMessageKey } from './metricSelectionMessages';

/**
 * Returned by `GET /twg-api/v1/microsite/metrics/org/{orgId}` when the
 * org is not present in the materialised table.
 *
 * The `/org/{orgId}` endpoint returns `404` with this body when the org
 * has not yet had metrics computed. The frontend treats this response as
 * `GETTING_STARTED` state, identical to the 200 `NO_DATA` shape.
 *
 * The request function passes `[200, 404]` as allowed statuses so this
 * body is received as `success: true`.
 */
export type NoDataResponse = {
	status: 'NO_DATA';
	organisationId: string;
};

/**
 * Response shape for `GET /twg-api/v1/microsite/metrics/org/{orgId}`.
 *
 * Source of truth: the BE controller in twg-api at
 * `src/app/controllers/microsite-metrics/types.ts` and the OpenAPI schema
 * `OrgMicrositeHeroMetricsResponse` in twg-api/openapi.yml.
 *
 * This is a superset of `GlobalMetricsResponse` — it includes every field
 * from the `/global` endpoint plus org-scoped metrics (Jira ↔ code links,
 * goal ↔ work item links, activity, pages, Loom usage, etc.). Required
 * fields (BE uses `requireNumber`/`requireString` which throw if the dbt
 * row is missing them, returning a 503 to the client) are non-nullable
 * here. The remaining fields can be `null` when the upstream dbt model
 * has not yet populated a value — treat `null` as "no data yet".
 */
export type OrgMetricsResponse = {
	organisationId: string;
	totalRelationships: number | null;
	activeRelTypes: number | null;
	jiraCodeLinks: number | null;
	goalWorkItemLinks: number | null;
	relDataAsOf: string | null;
	computedAt: string | null;
	cacheStatus: string | null;
	avgDailyRelChange: number | null;
	avgRelChangePerSecond: number | null;
	avgDailyJiraCodeLinkChange: number | null;
	avgJiraCodeLinkChangePerSecond: number | null;
	totalJiraIssues: number | null;
	avgArtifactsPerIssue: number | null;
	totalNodes: number | null;
	activeNodeTypes: number | null;
	teamCount: number | null;
	userCount: number | null;
	nodeDataAsOf: string | null;
	avgDailyNodeGrowth: number | null;
	avgNodeGrowthPerSecond: number | null;
	daysSinceSnapshot: number | null;
	estimatedNodesNow: number | null;
	activeContributors30d: number | null;
	activityDataAsOf: string | null;
	avgFetchQueriesPerDay: number | null;
	avgFetchQueriesPerSecond: number | null;
	queryDataAsOf: string | null;
	pagesCreated30d: number | null;
	pagesDataAsOf: string | null;
	loomsRecorded30d: number | null;
	totalMinutesRecorded: number | null;
	meetingMinutesSaved: number | null;
	loomDataAsOf: string | null;
	issuesMovedForward7d: number | null;
	issueActivityDataAsOf: string | null;

	// Comments & meeting notes
	commentsCreated7d: number | null;
	commentsDataAsOf: string | null;
	meetingNotesWithRecording7d: number | null;
	meetingNotesDataAsOf: string | null;

	// Connector ingest (Data Depot, last 30d)
	connectorObjects30d: number | null;
	connectorDataAsOf: string | null;
	connectorObjectsByProvider: Array<{
		providerName: string;
		objects30d: number;
	}> | null;

	// Smart-link pastes (last 30d)
	smartlinkPastes30d: number | null;
	smartlinkDataAsOf: string | null;

	// Connector adoption (Rovo 3P/Forge, latest snapshot)
	connectorsInstalled: number | null;
	connectorsActive28d: number | null;
	pctConnectorsActive28d: number | null;

	// Apps installed in the org (latest snapshot)
	onePAppsInstalled: number | null;
	marketplaceAddonsInstalled: number | null;
	threePConnectorsInstalled: number | null;
	forgeAppsInstalled: number | null;
	totalAppsInstalled: number | null;

	// Per-connector adoption detail + global benchmark + underutilised flag
	connectorsDetail: Array<{
		connectorName: string;
		connectorCategory: string;
		pctUsersAuthed28d: number;
		isUnderutilised: boolean;
		globalPctOrgsWithAnyAuthedUser: number;
	}> | null;

	// Per-user 'your graph' metrics — only populated when x-user-id AND x-cloudid
	// request headers are supplied.
	entitiesTouched: number | null;
	collaborators: number | null;
	collaboratorTeams: number | null;
	collaboratorTeamsTruncated: boolean | null;
};

/**
 * Fetch the org-scoped hero metrics used by the logged-in home view.
 *
 * Calls the Stargate-fronted route so the request is same-origin from the
 * microsite frontend. The `X-Query-Context` header carries the site ARI,
 * which the BE uses to authorise the caller against the requested org.
 *
 * The response is either the full metrics payload or a `NoDataResponse`
 * sentinel when the upstream dbt model has not yet produced rows for the
 * org — callers must handle both shapes.
 */
export const requestOrgMetrics = (
	orgId: string,
	cloudId: string,
	accountId?: string,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<OrgMetricsResponse | NoDataResponse>>;
} => {
	const headers: Record<string, string> = {
		'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
	};
	if (accountId) {
		headers['x-user-id'] = accountId;
		headers['x-cloudid'] = cloudId;
	}

	const { controller, execute } = requestWithAbort<OrgMetricsResponse | NoDataResponse>(
		'GET',
		`/gateway/api/twg-api/v1/microsite/metrics/org/${orgId}`,
		null,
		headers,
		[200, 404],
	);
	return { controller, execute };
};

/**
 * Identifies which section a metric belongs to.
 */
export type MetricSection = 'intro' | 'graphShape' | 'graphConnectivity';

/**
 * User experience state: whether to show metric sections or stay in getting-started state.
 */
export type ExperienceState = 'GETTING_STARTED' | 'SHOW_METRICS';

/**
 * String-literal union of all metric identifiers.
 */
export type MetricId =
	| 'totalNodes'
	| 'totalUsers'
	| 'totalRelationships'
	| 'totalApps'
	| 'pagesCreated'
	| 'loomsRecorded'
	| 'workItemUpdates'
	| 'queriesPerDay'
	| 'teamsConnected'
	| 'commentsAdded'
	| 'jiraCodeLinks'
	| 'smartLinkPastes'
	| 'artifactsPerIssue'
	| 'goalLinks'
	| 'activeContributors'
	| 'entitiesTouched'
	| 'uniqueCollaborators'
	| 'meetingsLinked'
	| 'usersActive'
	| 'designsCreated';

/**
 * Descriptor for a single metric in the pool.
 * Encodes metadata, priority, floor threshold, and the BE field to extract from.
 */
export type MetricDescriptor = {
	/**
	 * Unique identifier for this metric.
	 */
	id: MetricId;

	/**
	 * Section this metric belongs to.
	 */
	section: MetricSection;

	/**
	 * Priority within the section (lower number = higher priority).
	 * Used for ranking when space is limited.
	 */
	priority: number;

	/**
	 * Floor threshold: minimum value required for the metric to be selected.
	 * Use 'always' to include the metric regardless of value.
	 * Use a number for numeric comparison (value >= floor).
	 */
	floor: number | 'always';

	/**
	 * Key into metricSelectionMessages for retrieving i18n copy.
	 */
	copyKey: MetricSelectionMessageKey;

	/**
	 * Field name in OrgMetricsResponse to extract the value from.
	 * Use null if the field does not yet exist on the BE response.
	 */
	field: keyof OrgMetricsResponse | null;

	/**
	 * Whether this metric depends on a backing field not yet available on OrgMetricsResponse.
	 * When true, the metric is auto-excluded from selection until the backing field is added to the BE response.
	 */
	requiresField: boolean;

	/**
	 * Optional ordered list of fallback field names to try if the primary `field` is null
	 * or its value is not a number. Checked in order; the first numeric value wins.
	 * Only consulted when `requiresField` is false.
	 */
	fallbackFields?: readonly (keyof OrgMetricsResponse)[];

	/**
	 * Optional map of additional ICU placeholder names → `OrgMetricsResponse` field keys.
	 *
	 * Some copy strings reference more than just the primary `{value}` — e.g. the looms message
	 * also needs `{minutesSaved}`. List each placeholder here and the selector will resolve the
	 * value from `OrgMetricsResponse` and surface it on `SelectedMetric.values`.
	 *
	 * If any backing field is `null` or non-numeric, that placeholder is silently omitted from
	 * `values` (`react-intl` falls back to literal `{placeholder}` text — handle this in the
	 * descriptor design or copy by ensuring backing fields are stable).
	 *
	 * Example:
	 * ```ts
	 * extraValues: { minutesSaved: 'meetingMinutesSaved' }
	 * ```
	 */
	extraValues?: Readonly<Record<string, keyof OrgMetricsResponse>>;

	/**
	 * Optional icon to display alongside this metric in UI components.
	 */
	icon?: IconProp;
};

/**
 * A metric that has been selected and ranked.
 */
export type SelectedMetric = {
	/**
	 * Metric identifier.
	 */
	id: MetricId;

	/**
	 * Section this metric belongs to.
	 */
	section: MetricSection;

	/**
	 * Extracted numeric value from the metrics response.
	 */
	value: number;

	/**
	 * Key into metricSelectionMessages for i18n copy lookup.
	 * Use with react-intl's FormattedMessage or useIntl to resolve the ICU message.
	 */
	copyKey: string;

	/**
	 * Resolved ICU placeholder values for the copy, populated from the descriptor's
	 * `extraValues` map. Always passed to `<FormattedMessage values={...}>` (or `intl.formatMessage`)
	 * by consumers. Does NOT include the primary `value` — that lives on `SelectedMetric.value` and
	 * is the consumer's responsibility to pass through (typically as `{ value: stat.value, ...stat.values }`).
	 *
	 * Empty `{}` when the descriptor has no `extraValues`.
	 */
	values: Readonly<Record<string, number>>;
	icon?: IconProp;
};

/**
 * Result of metric selection and experience-state gating.
 *
 * Returned by `selectMetrics`. Consumers should check the `state` field:
 * - If `state === 'GETTING_STARTED'`, hide all metric sections and show an onboarding UI.
 * - If `state === 'SHOW_METRICS'`, render the three sections with their selected metrics.
 */
export type MetricSelectionResult = {
	/**
	 * Experience state: 'GETTING_STARTED' (no data or no valid metrics) or 'SHOW_METRICS' (metrics available).
	 * Callers should hide all graph sections when state is 'GETTING_STARTED'.
	 */
	state: ExperienceState;

	/**
	 * Selected metrics grouped by section, frozen for immutability.
	 * - `intro`: unlimited metrics (rendered in UserHighlightSection).
	 * - `graphShape`: 0–4 metrics (rendered in GraphGrowthSection).
	 * - `graphConnectivity`: 0–4 metrics (rendered in the connected apps section).
	 */
	sections: Readonly<Record<MetricSection, readonly SelectedMetric[]>>;
};

// ============================================================================
// Metric Descriptors (Pool)
// ============================================================================

/**
 * Intro section: 4 metrics describing overall graph size.
 * Floor: first three are 'always', totalApps floor is 2.
 * Note: totalNodes prefers 'estimatedNodesNow' with fallback to 'totalNodes'.
 * totalApps uses 'totalAppsInstalled' directly.
 */
export const INTRO_METRICS: readonly MetricDescriptor[] = [
	{
		id: 'totalNodes',
		section: 'intro',
		priority: 1,
		floor: 'always',
		copyKey: 'introTotalNodes',
		field: 'estimatedNodesNow',
		requiresField: false,
		fallbackFields: ['totalNodes'],
	},
	{
		id: 'totalUsers',
		section: 'intro',
		priority: 2,
		floor: 'always',
		copyKey: 'introTotalUsers',
		field: 'userCount',
		requiresField: false,
	},
	{
		id: 'totalRelationships',
		section: 'intro',
		priority: 3,
		floor: 'always',
		copyKey: 'introTotalRelationships',
		field: 'totalRelationships',
		requiresField: false,
	},
	{
		id: 'totalApps',
		section: 'intro',
		priority: 4,
		floor: 2,
		copyKey: 'introTotalApps',
		field: 'totalAppsInstalled',
		requiresField: false,
	},
];

/**
 * Graph shape section: 6 metrics describing growth and activity.
 * Max 12 metrics per layout rules.
 * Floor thresholds: pagesCreated 50, loomsRecorded 20, workItemUpdates 50,
 * queriesPerDay 1000, teamsConnected 5, commentsAdded 100.
 */
export const GRAPH_SHAPE_METRICS: readonly MetricDescriptor[] = [
	{
		id: 'pagesCreated',
		section: 'graphShape',
		priority: 1,
		floor: 50,
		copyKey: 'shapePagesCreated',
		field: 'pagesCreated30d',
		requiresField: false,
		icon: PageIcon,
	},
	{
		id: 'loomsRecorded',
		section: 'graphShape',
		priority: 2,
		floor: 20,
		copyKey: 'shapeLoomsRecorded',
		field: 'loomsRecorded30d',
		requiresField: false,
		extraValues: { minutesSaved: 'meetingMinutesSaved' },
		icon: VideoClipIcon,
	},
	{
		id: 'workItemUpdates',
		section: 'graphShape',
		priority: 3,
		floor: 50,
		copyKey: 'shapeWorkItemUpdates',
		field: 'issuesMovedForward7d',
		requiresField: false,
		icon: TaskIcon,
	},
	{
		id: 'queriesPerDay',
		section: 'graphShape',
		priority: 4,
		floor: 1000,
		copyKey: 'shapeQueriesPerDay',
		field: 'avgFetchQueriesPerDay',
		requiresField: false,
		icon: SearchIcon,
	},
	{
		id: 'teamsConnected',
		section: 'graphShape',
		priority: 5,
		floor: 5,
		copyKey: 'shapeTeamsConnected',
		field: 'teamCount',
		requiresField: false,
		icon: TeamsIcon,
	},
	{
		id: 'commentsAdded',
		section: 'graphShape',
		priority: 6,
		floor: 100,
		copyKey: 'shapeCommentsAdded',
		field: 'commentsCreated7d',
		requiresField: false,
		icon: CommentIcon,
	},
	{
		id: 'usersActive',
		section: 'graphShape',
		priority: 7,
		floor: 0,
		copyKey: 'shapeUsersActive',
		field: 'activeContributors30d',
		requiresField: false,
		icon: PeopleGroupIcon,
	},
	{
		id: 'jiraCodeLinks',
		section: 'graphShape',
		priority: 8,
		floor: 5,
		copyKey: 'shapeJiraCodeLinks',
		field: 'jiraCodeLinks',
		requiresField: false,
		icon: LinkIcon,
	},
];

/**
 * Graph connectivity section: 8 metrics describing links and collaboration.
 * Max 4 metrics per layout rules.
 * Floor thresholds: jiraCodeLinks 100, smartLinkPastes 100, artifactsPerIssue 2,
 * goalLinks 10, activeContributors 10, entitiesTouched 20, uniqueCollaborators 5,
 * meetingsLinked 10.
 */
export const GRAPH_CONNECTIVITY_METRICS: readonly MetricDescriptor[] = [
	{
		id: 'jiraCodeLinks',
		section: 'graphConnectivity',
		priority: 1,
		floor: 100,
		copyKey: 'connectivityJiraCodeLinks',
		field: 'jiraCodeLinks',
		requiresField: false,
	},
	{
		id: 'smartLinkPastes',
		section: 'graphConnectivity',
		priority: 2,
		floor: 100,
		copyKey: 'connectivitySmartLinkPastes',
		field: 'smartlinkPastes30d',
		requiresField: false,
	},
	{
		id: 'artifactsPerIssue',
		section: 'graphConnectivity',
		priority: 3,
		floor: 2,
		copyKey: 'connectivityArtifactsPerIssue',
		field: 'avgArtifactsPerIssue',
		requiresField: false,
	},
	{
		id: 'goalLinks',
		section: 'graphConnectivity',
		priority: 4,
		floor: 10,
		copyKey: 'connectivityGoalLinks',
		field: 'goalWorkItemLinks',
		requiresField: false,
	},
	{
		id: 'activeContributors',
		section: 'graphConnectivity',
		priority: 5,
		floor: 10,
		copyKey: 'connectivityActiveContributors',
		field: 'activeContributors30d',
		requiresField: false,
	},
	{
		id: 'entitiesTouched',
		section: 'graphConnectivity',
		priority: 6,
		floor: 20,
		copyKey: 'connectivityEntitiesTouched',
		field: 'entitiesTouched',
		requiresField: false,
	},
	{
		id: 'uniqueCollaborators',
		section: 'graphConnectivity',
		priority: 7,
		floor: 5,
		copyKey: 'connectivityUniqueCollaborators',
		field: 'collaborators',
		requiresField: false,
		extraValues: { teams: 'collaboratorTeams' },
	},
	{
		id: 'meetingsLinked',
		section: 'graphConnectivity',
		priority: 8,
		floor: 10,
		copyKey: 'connectivityMeetingsLinked',
		field: 'meetingNotesWithRecording7d',
		requiresField: false,
	},
];

/**
 * All metric descriptors concatenated for iteration/testing.
 */
export const ALL_METRIC_DESCRIPTORS: readonly MetricDescriptor[] = [
	...INTRO_METRICS,
	...GRAPH_SHAPE_METRICS,
	...GRAPH_CONNECTIVITY_METRICS,
];

// ============================================================================
// Selection and Gating Helpers
// ============================================================================

/**
 * Type guard to check if a response is a NoDataResponse.
 * Used to determine whether metrics data is available before selection.
 */
export const isNoDataResponse = (
	metrics: OrgMetricsResponse | unknown,
): metrics is { status: 'NO_DATA' } =>
	metrics != null &&
	typeof metrics === 'object' &&
	(metrics as { status?: string }).status === 'NO_DATA';

/**
 * Extract a numeric value from OrgMetricsResponse using a metric descriptor.
 *
 * Returns null if:
 * - The descriptor is marked `requiresField: true` (backing field not yet on OrgMetricsResponse).
 * - The field reference is null.
 * - The extracted value is not a number.
 *
 * Tries the primary field first, then falls back to any fields listed in `fallbackFields`
 * (checked in order); the first numeric value is returned.
 *
 * @param metrics The OrgMetricsResponse data to extract from.
 * @param descriptor The metric descriptor defining which field and requirements to check.
 * @returns The numeric value, or null if unavailable or gated.
 */
export const extractValue = (
	metrics: OrgMetricsResponse,
	descriptor: MetricDescriptor,
): number | null => {
	// Skip if the metric requires a field that doesn't exist yet.
	if (descriptor.requiresField || descriptor.field === null) {
		return null;
	}

	// Try the primary field first.
	const primary = metrics[descriptor.field];
	if (typeof primary === 'number') {
		return primary;
	}

	// Try fallback fields in order.
	if (descriptor.fallbackFields) {
		for (const fallbackField of descriptor.fallbackFields) {
			const fallback = metrics[fallbackField];
			if (typeof fallback === 'number') {
				return fallback;
			}
		}
	}

	return null;
};

/**
 * Check if a numeric value passes the floor threshold for a metric.
 *
 * A floor of 'always' will pass any value. Otherwise, the value must be
 * greater than or equal to the numeric threshold.
 *
 * @param value The numeric value to check.
 * @param floor The floor threshold from the metric descriptor ('always' or a number).
 * @returns True if the value meets the floor requirement; false otherwise.
 */
export const passesFloor = (value: number, floor: MetricDescriptor['floor']): boolean => {
	if (floor === 'always') {
		return true;
	}
	return value >= floor;
};

/**
 * Resolve the `extraValues` map on a descriptor against `OrgMetricsResponse`.
 *
 * Returns an object whose keys are ICU placeholder names and whose values are the corresponding
 * numeric values from the response. Placeholders whose backing field is `null` or non-numeric are
 * silently dropped — `react-intl` will render the literal `{placeholder}` token if a key is missing,
 * which makes that case visible in QA without breaking selection.
 *
 * Returns an empty object when `descriptor.extraValues` is undefined.
 */
export const extractExtraValues = (
	metrics: OrgMetricsResponse,
	descriptor: MetricDescriptor,
): Readonly<Record<string, number>> => {
	if (!descriptor.extraValues) {
		return {};
	}
	const resolved: Record<string, number> = {};
	for (const [placeholder, field] of Object.entries(descriptor.extraValues)) {
		const raw = metrics[field];
		if (typeof raw === 'number') {
			resolved[placeholder] = raw;
		}
	}
	return resolved;
};

// ============================================================================
// Experience State Evaluation
// ============================================================================

/**
 * Evaluate whether the org passes the Tier 2 gate for showing org-level metrics.
 *
 * Tier 2 criteria (Hero Metrics model, 2026-04-29):
 *   - userCount >= 10 (paid seats)
 *   - totalRelationships >= 5,000
 *
 * If either threshold is not met, falls through to Tier 3 (GettingStartedView).
 * If metrics are null or NO_DATA, also falls to Tier 3.
 *
 * @param metrics The OrgMetricsResponse data, or null/NoDataResponse if unavailable.
 * @returns 'GETTING_STARTED' if org does not meet Tier 2 thresholds, 'SHOW_METRICS' if it does.
 */
export const evaluateOrgTier = (metrics: OrgMetricsResponse | unknown | null): ExperienceState => {
	if (metrics == null || isNoDataResponse(metrics)) {
		return 'GETTING_STARTED';
	}
	const orgMetrics = metrics as OrgMetricsResponse;
	if ((orgMetrics.userCount ?? 0) >= 10 && (orgMetrics.totalRelationships ?? 0) >= 5000) {
		return 'SHOW_METRICS';
	}
	return 'GETTING_STARTED';
};

/**
 * Evaluate the experience state (Step 1 gating) based on metrics availability and section content.
 *
 * **Gating rules:**
 * - Returns `GETTING_STARTED` if metrics is null or a NoDataResponse (no data available).
 * - Returns `GETTING_STARTED` if the org does not pass the Tier 2 scale gate (via `evaluateOrgTier`).
 * - Returns `GETTING_STARTED` if all three sections (intro, graphShape, graphConnectivity) are empty.
 * - Returns `SHOW_METRICS` otherwise.
 *
 * This gate controls whether the metric sections should render. Callers should hide all graph
 * sections when state is `GETTING_STARTED`.
 *
 * @param metrics The OrgMetricsResponse data, or null/NoDataResponse if unavailable.
 * @param selectedSections Optional pre-evaluated section results; if provided, used to check for empty sections.
 * @returns 'GETTING_STARTED' (no data or no valid metrics) or 'SHOW_METRICS' (data and metrics available).
 */
export const evaluateExperienceState = (
	metrics: OrgMetricsResponse | unknown | null,
	selectedSections?: Readonly<Record<MetricSection, readonly SelectedMetric[]>>,
): ExperienceState => {
	// If metrics is null or NoDataResponse, return GETTING_STARTED.
	if (metrics == null || isNoDataResponse(metrics)) {
		return 'GETTING_STARTED';
	}

	// Tier 2 scale gate: org must have >= 10 seats and >= 5,000 relationships.
	if (evaluateOrgTier(metrics) === 'GETTING_STARTED') {
		return 'GETTING_STARTED';
	}

	// If selected sections are provided and all are empty, return GETTING_STARTED.
	if (selectedSections) {
		const allEmpty =
			selectedSections.intro.length === 0 &&
			selectedSections.graphShape.length === 0 &&
			selectedSections.graphConnectivity.length === 0;
		if (allEmpty) {
			return 'GETTING_STARTED';
		}
	}

	return 'SHOW_METRICS';
};

// ============================================================================
// Metric Selection
// ============================================================================

/**
 * Select and rank metrics from OrgMetricsResponse (Step 2 of the metric pipeline).
 *
 * **Two-step pipeline:**
 * 1. **Gating (Step 1)**: Determine if metrics data is available via `evaluateExperienceState`.
 * 2. **Selection (Step 2)**: For each metric descriptor in priority order, extract value, apply floor,
 *    and collect up to the section cap.
 *
 * **Algorithm:**
 * - If metrics is null or NoDataResponse, return empty sections with state = 'GETTING_STARTED'.
 * - For each descriptor in order (INTRO_METRICS → GRAPH_SHAPE_METRICS → GRAPH_CONNECTIVITY_METRICS):
 *   - Extract numeric value using `extractValue`.
 *   - Skip if value is null or fails the floor threshold (via `passesFloor`).
 *   - Add to the corresponding section up to its cap.
 * - Evaluate experience state and return frozen result.
 *
 * **Section caps:**
 * - `intro`: unlimited metrics (rendered in UserHighlightSection).
 * - `graphShape`: max 4 metrics (rendered in GraphGrowthSection).
 * - `graphConnectivity`: max 4 metrics (rendered in the connected apps section).
 *
 * **Output:**
 * - `state`: 'GETTING_STARTED' if no data or all sections empty; 'SHOW_METRICS' otherwise.
 * - `sections`: Frozen record of metric arrays; each `SelectedMetric` includes a `copyKey` for i18n lookup.
 *
 * @param metrics The OrgMetricsResponse data, or null/NoDataResponse if unavailable.
 * @returns MetricSelectionResult with state and per-section metric arrays.
 */
export const selectMetrics = (
	metrics: OrgMetricsResponse | unknown | null,
): MetricSelectionResult => {
	// If metrics is null or NoDataResponse, return empty result with GETTING_STARTED state.
	if (metrics == null || isNoDataResponse(metrics)) {
		const emptySections = {
			intro: [] as const,
			graphShape: [] as const,
			graphConnectivity: [] as const,
		};
		return {
			state: 'GETTING_STARTED',
			sections: Object.freeze(emptySections) as Readonly<
				Record<MetricSection, readonly SelectedMetric[]>
			>,
		};
	}

	// Cast to OrgMetricsResponse now that we know it's not null/NoData.
	const metricsData = metrics as OrgMetricsResponse;

	// Section capacity limits.
	const sectionCaps: Record<MetricSection, number> = {
		intro: Infinity,
		graphShape: 8,
		graphConnectivity: 4,
	};

	// Collect selected metrics per section.
	const selectedBySection: Record<MetricSection, SelectedMetric[]> = {
		intro: [],
		graphShape: [],
		graphConnectivity: [],
	};

	// Process each descriptor array in order.
	const descriptorArrays = [INTRO_METRICS, GRAPH_SHAPE_METRICS, GRAPH_CONNECTIVITY_METRICS];
	for (const descriptorArray of descriptorArrays) {
		for (const descriptor of descriptorArray) {
			const section = descriptor.section;

			// Skip if we've already hit the cap for this section.
			if (selectedBySection[section].length >= sectionCaps[section]) {
				continue;
			}

			// Extract the value.
			const value = extractValue(metricsData, descriptor);

			// Skip if value is null or doesn't pass the floor.
			if (value === null || !passesFloor(value, descriptor.floor)) {
				continue;
			}

			// Build and add the selected metric. `values` carries any extra ICU placeholders
			// resolved from `descriptor.extraValues` so consumers can spread them straight into
			// `<FormattedMessage values={{ value: stat.value, ...stat.values }} />`.
			const selected: SelectedMetric = {
				id: descriptor.id,
				section,
				value,
				copyKey: descriptor.copyKey as string,
				values: Object.freeze(extractExtraValues(metricsData, descriptor)),
				icon: descriptor.icon,
			};

			selectedBySection[section].push(selected);
		}
	}

	// Convert to readonly record and evaluate state.
	const sections: Readonly<Record<MetricSection, readonly SelectedMetric[]>> = {
		intro: Object.freeze(selectedBySection.intro),
		graphShape: Object.freeze(selectedBySection.graphShape),
		graphConnectivity: Object.freeze(selectedBySection.graphConnectivity),
	};

	const state = evaluateExperienceState(metricsData, sections);

	return {
		state,
		sections: Object.freeze(sections),
	};
};

export const selectAllMetrics = (
	metrics: OrgMetricsResponse | unknown | null,
): OrgMetricsResponse | null => {
	if (metrics == null || isNoDataResponse(metrics)) {
		return null;
	}

	return metrics as OrgMetricsResponse;
};
